import { useId, useState, type ReactNode, type SelectHTMLAttributes, type InputHTMLAttributes, type TextareaHTMLAttributes } from 'react';

/**
 * Campos de formulário com rótulo associado por `htmlFor`.
 *
 * O controle fica fora do `<label>` de propósito: aninhado, o texto acessível
 * do rótulo passa a incluir o conteúdo do controle — as opções inteiras de um
 * `<select>`, por exemplo — e deixa de identificar o campo para leitores de
 * tela e para quem automatiza a interface.
 */

// Medidas do protótipo aprovado: borda fria, cantos de 9px e o rótulo em
// caixa-alta miúda acima do controle.
const CLASSE_CAIXA =
  'w-full rounded-[9px] border border-[#d7e0e6] bg-superficie px-2.75 py-2.5 outline-none focus:border-marinho-claro';

const CLASSE_CONTROLE = `mt-1.25 ${CLASSE_CAIXA}`;

const CLASSE_ROTULO = 'text-[11px] font-extrabold text-[#536575]';

type PropsComuns = { rotulo: string; className?: string; dica?: string };

export function CampoTexto({
  rotulo,
  className,
  dica,
  ...props
}: PropsComuns & InputHTMLAttributes<HTMLInputElement>): React.JSX.Element {
  const id = useId();

  return (
    <div className={className}>
      <label htmlFor={id} className={CLASSE_ROTULO}>
        {rotulo}
      </label>
      <input id={id} className={CLASSE_CONTROLE} {...props} />
      {dica ? <Dica>{dica}</Dica> : null}
    </div>
  );
}

/** Campo de senha com o botão de mostrar o que foi digitado. */
export function CampoSenha({
  rotulo,
  className,
  dica,
  ...props
}: PropsComuns & Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>): React.JSX.Element {
  const id = useId();

  return (
    <div className={className}>
      <label htmlFor={id} className={CLASSE_ROTULO}>
        {rotulo}
      </label>
      <div className="mt-1.25">
        <EntradaDeSenha id={id} className={CLASSE_CAIXA} {...props} />
      </div>
      {dica ? <Dica>{dica}</Dica> : null}
    </div>
  );
}

/**
 * O controle de senha sem rótulo, para a tela de entrar, que tem desenho
 * próprio.
 *
 * Mostrar a senha poupa o erro de digitação que só se descobre depois, e no
 * celular esse erro é o mais comum. O nome do botão é fixo e o estado vai em
 * `aria-pressed`: trocar o nome a cada toque faria o leitor de tela anunciar um
 * botão diferente em vez de dizer que o mesmo botão foi ligado.
 */
export function EntradaDeSenha({
  className,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>): React.JSX.Element {
  const [visivel, definirVisivel] = useState(false);

  return (
    <div className="relative">
      <input {...props} type={visivel ? 'text' : 'password'} className={`${className ?? ''} pr-11`} />
      <button
        type="button"
        aria-label="Mostrar senha"
        aria-pressed={visivel}
        aria-controls={props.id}
        title={visivel ? 'Ocultar senha' : 'Mostrar senha'}
        onClick={() => definirVisivel((atual) => !atual)}
        className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-[9px] text-texto-suave transition hover:text-marinho focus-visible:text-marinho"
      >
        <IconeDeOlho riscado={visivel} />
      </button>
    </div>
  );
}

/** O olho aberto convida a mostrar; riscado, a esconder de novo. */
function IconeDeOlho({ riscado }: { riscado: boolean }): React.JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12Z" />
      <circle cx="12" cy="12" r="3" />
      {riscado ? <path d="M3 3l18 18" /> : null}
    </svg>
  );
}

/** Explicação curta abaixo do controle, no tom do protótipo. */
function Dica({ children }: { children: ReactNode }): React.JSX.Element {
  return <span className="mt-1 block text-[11px] leading-snug text-texto-suave">{children}</span>;
}

export function CampoSelecao({
  rotulo,
  className,
  dica,
  children,
  ...props
}: PropsComuns & SelectHTMLAttributes<HTMLSelectElement> & { children: ReactNode }): React.JSX.Element {
  const id = useId();

  return (
    <div className={className}>
      <label htmlFor={id} className={CLASSE_ROTULO}>
        {rotulo}
      </label>
      <select id={id} className={CLASSE_CONTROLE} {...props}>
        {children}
      </select>
      {dica ? <Dica>{dica}</Dica> : null}
    </div>
  );
}

export function CampoTextoLongo({
  rotulo,
  className,
  dica,
  ...props
}: PropsComuns & TextareaHTMLAttributes<HTMLTextAreaElement>): React.JSX.Element {
  const id = useId();

  return (
    <div className={className}>
      <label htmlFor={id} className={CLASSE_ROTULO}>
        {rotulo}
      </label>
      <textarea id={id} className={`${CLASSE_CONTROLE} min-h-[85px] resize-y`} {...props} />
      {dica ? <Dica>{dica}</Dica> : null}
    </div>
  );
}

export function CampoCaixa({
  rotulo,
  className,
  ...props
}: PropsComuns & InputHTMLAttributes<HTMLInputElement>): React.JSX.Element {
  const id = useId();

  return (
    <div className={`flex items-center gap-2 ${className ?? ''}`}>
      <input id={id} type="checkbox" className="size-4" {...props} />
      <label htmlFor={id} className={CLASSE_ROTULO}>
        {rotulo}
      </label>
    </div>
  );
}
